import { buildPayload } from "./event-payload.js";
import { isExcludedPage } from "./url-match-core.js";
import { isShopifyContextCapture, publishShopifyContext } from "./shopify-context.js";
/**
 * Event sending with multi-fallback strategy (sendBeacon → fetch+keepalive → fetch)
 * and sessionStorage buffering for failed sends.
 */

import { state, debugLog } from "./state.js";
import {
  STORAGE_KEY_BUFFER,
  BUFFER_MAX_ITEMS,
  BUFFER_MAX_AGE_MS,
  FALLBACK_INGEST_URL,
} from "./constants.js";
import { fireFacebookPixel } from "./facebook-pixel.js";
import { fireGoogleAdsPixel, fireBingPixel, fireLinkedInPixel, fireTikTokPixel, fireGA4Pixel, fireSnapchatPixel, firePinterestPixel, fireRedditPixel, fireOpenAIAdsPixel } from "./platforms.js";
import type { EventData, EventPayload } from "./types.js";
import { isAnalyticsAllowed, isMarketingAllowed, getParamsToRemove, consentIsDecided } from "./consent.js";
import { markRuleFired } from "./conversion-dedup.js";
import { pixelManagerOwnsWooMetaEvent } from "./meta-browser-owner.js";

/** Buffer a failed event in sessionStorage for later replay. */
function bufferEvent(payload: EventPayload): void {
  try {
    const buffer: EventPayload[] = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY_BUFFER) || "[]",
    );
    payload._bufferedAt = Date.now();
    if (buffer.length < BUFFER_MAX_ITEMS) buffer.push(payload);
    sessionStorage.setItem(STORAGE_KEY_BUFFER, JSON.stringify(buffer));
  } catch {
    /* sessionStorage not available */
  }
}

/**
 * Kan deze browser een mislukte verzending herkennen?
 *
 * `navigator.sendBeacon` geeft `true` zodra de BROWSER het pakketje in de
 * wachtrij zet, niet als het aankomt. Gemeten: `true` naar een niet-bestaand
 * domein en `true` naar een 404. Bij een storing is dat dus altijd `true`,
 * waarna de hele fallback-keten eronder wegvalt: de edge-failover die juist
 * voor precies dat moment is gebouwd werd nooit aangeroepen, en het script
 * meldde intussen `sent: true`.
 *
 * `fetch` met `keepalive` overleeft page-unload net zo goed (het is de moderne
 * vervanger van sendBeacon) maar geeft wél een promise die faalt. Waar dat
 * beschikbaar is nemen we die, en pas als het er niet is valt sendBeacon in:
 * op zo'n browser is blind versturen nog altijd beter dan een fetch die bij
 * het verlaten van de pagina wordt afgekapt.
 */
const KEEPALIVE_SUPPORTED: boolean = (() => {
  try {
    return typeof Request !== "undefined" && "keepalive" in Request.prototype;
  } catch {
    return false;
  }
})();

function sendViaFetch(
  url: string,
  body: string,
  keepalive: boolean,
): Promise<Response> {
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive,
    credentials: "include",
  });
}

/**
 * Is dit antwoord een reden om naar het volgende vangnet te gaan?
 *
 * `fetch` verwerpt alleen bij een netwerkfout. Een server die leeft maar 500
 * teruggeeft (proces draait, database weg) levert een keurig opgelost antwoord
 * op, en dan zou de keten hieronder alsnog niet draaien: het script zou de
 * conversie als afgehandeld beschouwen terwijl wij hem niet hebben aangenomen.
 *
 * Vanaf 500 dus wél doorschuiven naar de edge. Die bewaart hem en biedt hem
 * later opnieuw aan, en de primaire ontdubbelt op (websiteId, eventId).
 *
 * 4xx juist niet. Dat is een bewuste weigering (ongeldige payload, 429 omdat we
 * afknijpen); nog een keer aanbieden verandert daar niets aan en zou het
 * vangnet vullen met werk dat nooit gaat lukken.
 */
function isServerFalen(response: Response): boolean {
  return response.status >= 500;
}

/** A rule can opt a platform out of an event (send: false in the event
 * mapping); the config lists those under platforms[x].blockedEvents. The
 * browser pixel must respect that too, or a deselected platform still
 * receives the event through the client channel. */
function pixelBlocked(platform: string, eventName: string): boolean {
  const platforms = state.config && state.config.platforms;
  const platformConfig = platforms && (platforms as Record<string, { blockedEvents?: string[] }>)[platform];
  const blocked = platformConfig && platformConfig.blockedEvents;
  if (!blocked || !Array.isArray(blocked)) return false;
  return blocked.indexOf(eventName) !== -1;
}

/** Per-rule platform selection: a rule that fans out to a subset of
 * platforms must gate the browser pixel too (the server gates CAPI with the
 * same rule), or a deselected platform still receives the event through the
 * client channel. Rules without a platforms array fire everything (legacy). */
function ruleAllowsPixel(platform: string, payload: EventPayload): boolean {
  if (!payload.trackingRuleId) return true;
  const rules = state.config && state.config.rules;
  if (!rules) return true;
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    if (rule.id !== payload.trackingRuleId) continue;
    if (!rule.platforms) return true;
    return rule.platforms.indexOf(platform) !== -1;
  }
  return true;
}

function pixelAllowed(platform: string, eventName: string, payload: EventPayload): boolean {
  if (platform === "facebook" && pixelManagerOwnsWooMetaEvent(state.metaWooBrowserExternal, eventName)) return false;
  if (pixelBlocked(platform, eventName)) return false;
  return ruleAllowsPixel(platform, payload);
}

/** Fire browser pixels for all configured platforms (dual-path dedup with server-side). */
function fireBrowserPixels(payload: EventPayload, eventId: string, capturedUrl: string): void {
  const eventName = payload.eventName;
  if (!eventName) return;

  if (pixelAllowed("facebook", eventName, payload)) fireFacebookPixel(payload, eventId, capturedUrl);
  if (pixelAllowed("google_ads", eventName, payload)) fireGoogleAdsPixel(payload, eventId);
  if (pixelAllowed("openai_ads", eventName, payload)) fireOpenAIAdsPixel(payload, eventId);
  if (pixelAllowed("bing_ads", eventName, payload)) fireBingPixel(payload);
  if (pixelAllowed("linkedin", eventName, payload)) fireLinkedInPixel(payload, eventId);
  if (pixelAllowed("tiktok", eventName, payload)) fireTikTokPixel(payload, eventId);
  if (pixelAllowed("ga4", eventName, payload)) fireGA4Pixel(payload, eventId);
  if (pixelAllowed("snapchat", eventName, payload)) fireSnapchatPixel(payload, eventId);
  if (pixelAllowed("pinterest", eventName, payload)) firePinterestPixel(payload, eventId);
  if (pixelAllowed("reddit", eventName, payload)) fireRedditPixel(payload, eventId);
}

/**
 * Verzenden met een keten van terugvalopties:
 * 1. fetch met keepalive (overleeft page-unload EN vertelt of het aankwam)
 * 2. gewone fetch
 * 3. de duurzame edge-ingest (Cloudflare Worker, eigen faaldomein)
 * 4. bufferen in sessionStorage
 *
 * sendBeacon stond hier ooit bovenaan. Dat leek de veiligste keuze maar was het
 * tegendeel: het kan niet melden dat er iets mis is, dus stap 2 tot en met 4
 * draaiden nooit als de origin plat lag. Zie KEEPALIVE_SUPPORTED hierboven.
 */
function sendWithFallbacks(
  trackUrl: string,
  body: string,
  payload: EventPayload,
  callback?: (error: unknown, result?: unknown) => void,
): void {
  // Alleen op browsers zonder keepalive-fetch: blind versturen is daar beter
  // dan een fetch die bij het verlaten van de pagina wordt afgekapt. Op elke
  // moderne browser slaan we dit over, want een geslaagde sendBeacon zegt
  // niets over aankomst en zou de rest van deze keten uitschakelen.
  if (!KEEPALIVE_SUPPORTED && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(trackUrl, blob)) {
      if (callback) callback(null, { eventId: payload.eventId, sent: true });
      return;
    }
  }

  // Primair: fetch met keepalive
  sendViaFetch(trackUrl, body, true)
    .then((response) => {
      if (isServerFalen(response)) throw new Error("primary " + response.status);
      return response.json();
    })
    .then((result) => {
      if (callback) callback(null, result);
    })
    .catch(() => {
      // Terugval 1: gewone fetch
      sendViaFetch(trackUrl, body, false)
        .then((response) => {
          if (isServerFalen(response)) throw new Error("primary " + response.status);
          return response.json();
        })
        .then((result) => {
          if (callback) callback(null, result);
        })
        .catch((error) => {
          // Terugval 2: de duurzame edge-ingest (Cloudflare Worker), een eigen
          // faaldomein. Pas daarna lokaal bufferen.
          sendToEdgeOrBuffer(body, payload, error, callback);
        });
    });
}

/**
 * Final failover: the primary is unreachable, so send to the durable edge ingest
 * endpoint (which buffers + replays to the primary once it recovers). If the edge
 * is unset or also fails, buffer locally in sessionStorage as the last resort.
 */
function sendToEdgeOrBuffer(
  body: string,
  payload: EventPayload,
  primaryError: unknown,
  callback?: (error: unknown, result?: unknown) => void,
): void {
  if (!FALLBACK_INGEST_URL) {
    bufferEvent(payload);
    if (callback) callback(primaryError);
    return;
  }

  sendViaFetch(FALLBACK_INGEST_URL, body, true)
    .then((response) => {
      if (isServerFalen(response)) throw new Error("edge " + response.status);
      return response.json();
    })
    .then((result) => {
      if (callback) callback(null, result);
    })
    .catch((edgeError) => {
      bufferEvent(payload);
      if (callback) callback(edgeError);
    });
}

/** Emit a CustomEvent so external code can observe tracking activity. */
function emitTrackingEvent(payload: EventPayload): void {
  if (!window.dispatchEvent) return;
  window.dispatchEvent(
    new CustomEvent("thesocialtap:event", {
      detail: {
        eventId: payload.eventId,
        eventName: payload.eventName || "PageView",
        pageUrl: payload.pageUrl,
        timestamp: Date.now(),
        payload,
      },
    }),
  );
}

/**
 * Events die wachten op een keuze van de bezoeker.
 *
 * Klein en alleen voor deze paginaweergave: dit is bedoeld om het gat tussen
 * "pagina geladen" en "banner weggeklikt" te overbruggen, niet om een archief
 * bij te houden. Wie nooit iets kiest, verstuurt nooit iets.
 */
const MAX_PARKED_EVENTS = 20;
let parkedEvents: Array<EventData | undefined> = [];

function parkEvent(data: EventData | undefined): void {
  if (parkedEvents.length >= MAX_PARKED_EVENTS) return;
  parkedEvents.push(data);
  debugLog("sendEvent", "parked until consent is decided, queue=" + parkedEvents.length);
}

/**
 * Alsnog versturen wat er tijdens het wachten is opgelopen.
 *
 * Wordt aangeroepen zodra de toestemming verandert. Bij een weigering leegt de
 * rij zonder te versturen: de bezoeker heeft dan nee gezegd, en dat geldt ook
 * voor wat hij daarvoor deed.
 */
export function flushParkedEvents(): void {
  const pending = parkedEvents;
  parkedEvents = [];
  if (pending.length === 0) return;

  if (!isAnalyticsAllowed()) {
    debugLog("flushParkedEvents", "discarded " + pending.length + " events, consent denied");
    return;
  }

  debugLog("flushParkedEvents", "sending " + pending.length + " events held for consent");
  for (const data of pending) {
    sendEvent(data);
  }
}

/**
 * Stamp a once-per-visitor rule as converted, at the ONE spot where the event
 * actually leaves the browser. Every trigger path used to stamp next to its
 * own sendEvent call, which stamped events that sendEvent then PARKED on
 * undecided consent: the visitor was permanently marked converted while
 * nothing was ever delivered, and every later real conversion was skipped.
 * Stamping here also covers the parked events that flushParkedEvents
 * re-offers after a banner accept, which no call site could see.
 */
function markDispatchedConversion(data: EventData | undefined): void {
  if (!data || !data.trackingRuleId) return;
  const rules = state.config && state.config.rules;
  if (!rules) return;
  for (let i = 0; i < rules.length; i++) {
    if (rules[i].id === data.trackingRuleId) {
      markRuleFired(rules[i].id, rules[i].oncePerVisitor);
      return;
    }
  }
}

/** Send event to server (dual-path: browser pixels + CAPI). */
/** Whether this event, or the page it is sent from, is an excluded page. */
function onExcludedPage(data: EventData | undefined): boolean {
  const patterns = state.config?.excludedPages;
  if (!patterns || patterns.length === 0) return false;
  if (isExcludedPage(window.location.href, patterns)) return true;
  return !!data?.pageUrl && isExcludedPage(data.pageUrl, patterns);
}

export function sendEvent(
  data: EventData | undefined,
  callback?: (error: unknown, result?: unknown) => void,
): void {
  if (!state.websiteId) return;

  // A page the customer excluded sends nothing at all, and is not parked for
  // later either: a parked event would leave the moment consent arrived.
  if (onExcludedPage(data)) {
    debugLog("sendEvent", "excluded page, skipping");
    if (callback) callback(null, { eventId: "", sent: false, excludedPage: true });
    return;
  }

  // Consent check: if analytics denied, don't track at all
  if (!isAnalyticsAllowed()) {
    // Nog niets gekozen is iets anders dan nee. Wie de banner nog voor zich
    // heeft, heeft ons niets geweigerd; die paginaweergave weggooien betekende
    // dat elke klant met een banner structureel zijn eerste weergave miste, en
    // dat is precies de weergave waar de advertentieklik aan hangt.
    if (!consentIsDecided()) parkEvent(data);
    debugLog("sendEvent", "analytics consent denied, skipping");
    if (callback) callback(null, { eventId: "", sent: false, consentDenied: true });
    return;
  }

  const payload = buildPayload(data);
  if (isShopifyContextCapture(data)) {
    // The app pixel supplies the real Shopify event ID. This request enriches
    // it without firing another browser tag or creating a billable event.
    publishShopifyContext(payload, false);
    sendWithFallbacks(state.apiUrl + "/events/track", JSON.stringify(payload), payload, (error, result) => {
      const received = result as { eventName?: string } | undefined;
      if (!error && received?.eventName === "ShopifyContext") publishShopifyContext(payload, true);
      if (callback) callback(error, result);
    });
    return;
  }
  markDispatchedConversion(data);
  debugLog("sendEvent", "eventId=" + payload.eventId, "eventName=" + (payload.eventName || "PageView"), "pageUrl=" + payload.pageUrl, "browserPixelFired=" + payload.browserPixelFired);

  // Strip configured URL params from pageUrl. tst_em (e-mail-link tracking)
  // is ALWAYS stripped: it can carry a raw address and the page URL travels
  // to every ad platform as event_source_url.
  const paramsToRemove = getParamsToRemove().concat(["tst_em"]);
  try {
    const url = new URL(payload.pageUrl);
    for (let i = 0; i < paramsToRemove.length; i++) {
      url.searchParams.delete(paramsToRemove[i]);
    }
    payload.pageUrl = url.toString();
  } catch { /* invalid URL, keep as-is */ }

  // Only fire browser pixels if marketing consent is granted
  if (isMarketingAllowed()) {
    fireBrowserPixels(payload, payload.eventId, payload.pageUrl);
  } else {
    debugLog("sendEvent", "marketing consent denied, skipping browser pixels");
  }

  if (payload.pixelsFired.length === 0) delete (payload as Partial<EventPayload>).pixelsFired;

  // Conflicts ride along on the event rather than on their own request. They
  // are rare, they only matter next to the events they distort, and a separate
  // call would be one more thing to fail quietly.
  if (state.pixelConflicts.length > 0) payload.pixelConflicts = state.pixelConflicts.slice();

  emitTrackingEvent(payload);

  const trackUrl = state.apiUrl + "/events/track";
  sendWithFallbacks(trackUrl, JSON.stringify(payload), payload, callback);
}

/**
 * De gebufferde events opnieuw aanbieden bij het laden van de pagina.
 *
 * Dit is de laatste opslag die we hebben: wat hier staat is al één keer niet
 * aangekomen. Ze werden hier weggegooid zodra `sendBeacon` `true` gaf, en dat
 * zegt alleen dat de browser het pakketje heeft aangenomen. Tijdens precies de
 * storing waarvoor deze buffer bestaat werd hij dus leeggemaakt zonder dat er
 * iets was afgeleverd.
 *
 * Nu blijft een event staan tot een antwoord bewijst dat het binnen is. Bij
 * twijfel houden we hem, want de server ontdubbelt op (websiteId, eventId): een
 * dubbel aangeboden conversie kost niets, een weggegooide kost geld.
 *
 * Het terugschrijven gebeurt pas als alle pogingen klaar zijn. Wordt de pagina
 * daarvoor verlaten, dan blijft de buffer staan zoals hij was en proberen we
 * het de volgende keer opnieuw. Ook dat valt de goede kant op.
 */
export function replayBufferedEvents(): void {
  try {
    const buffer: EventPayload[] = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY_BUFFER) || "[]",
    );
    if (buffer.length === 0) return;

    const now = Date.now();
    const vers = buffer.filter(
      (event) => now - (event._bufferedAt || 0) <= BUFFER_MAX_AGE_MS,
    );
    if (vers.length === 0) {
      sessionStorage.setItem(STORAGE_KEY_BUFFER, "[]");
      return;
    }

    const trackUrl = state.apiUrl + "/events/track";
    const pogingen = vers.map((bufferedEvent) => {
      const bewaardeStempel = bufferedEvent._bufferedAt;
      delete bufferedEvent._bufferedAt;
      const body = JSON.stringify(bufferedEvent);
      // Terugzetten: mislukt de poging, dan moet de leeftijd bewaard blijven,
      // anders leeft een event dat nooit aankomt eeuwig door.
      bufferedEvent._bufferedAt = bewaardeStempel;
      return sendViaFetch(trackUrl, body, true)
        .then((response) => (response.ok ? null : bufferedEvent))
        .catch(() => bufferedEvent);
    });

    Promise.all(pogingen)
      .then((uitkomsten) => {
        const rest = uitkomsten.filter((event): event is EventPayload => event !== null);
        try {
          sessionStorage.setItem(STORAGE_KEY_BUFFER, JSON.stringify(rest));
        } catch {
          /* sessionStorage niet beschikbaar */
        }
      })
      .catch(() => {
        /* Promise.all kan hier niet falen; de buffer blijft dan staan. */
      });
  } catch {
    /* sessionStorage not available */
  }
}
